import React, { useState, useContext } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { useLocation, useHistory } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import PasswordInput from '../components/common/PasswordInput';
import PasswordRequirements from '../components/common/PasswordRequirements';
import { validatePassword } from '../utils/validatePassword';

export default function ResetPassword() {
  const location = useLocation();
  const history = useHistory();
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState(
    location.state?.email ? `A 6-digit reset code has been sent to ${location.state.email}. It expires in 15 minutes.` : ''
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword, forgotPassword } = useContext(AuthContext);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setMessage('');
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    if (!validatePassword(password)) {
      setError('Password does not meet the requirements below.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email, code, password);
      setMessage('Password has been reset. You may now log in.');
      setTimeout(() => history.push('/'), 2000);
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors?.length) {
        // Password policy failure — the code is still valid, stay on this step.
        setError(data.errors.join(' '));
      } else if (data?.msg) {
        // Invalid/expired code or rate limited — server msg explains next steps.
        setError(data.msg);
      } else {
        setError('Failed to reset password.');
      }
    }
    setLoading(false);
  }

  async function handleResend() {
    setError('');
    setMessage('');
    if (!email) {
      setError('Enter your email to receive a reset code.');
      return;
    }
    setLoading(true);
    try {
      await forgotPassword(email);
      setCode('');
      setMessage(`A new 6-digit reset code has been sent to ${email}. It expires in 15 minutes.`);
    } catch (err) {
      setError('Failed to send a new code. Please try again.');
    }
    setLoading(false);
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2 className="auth-title">Reset Password</h2>
        {error && <Alert variant="danger">{error}</Alert>}
        {message && <Alert variant="success">{message}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Form.Group id="email">
            <Form.Label>Email</Form.Label>
            <Form.Control className="auth-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </Form.Group>
          <Form.Group id="code">
            <Form.Label>Reset Code</Form.Label>
            <Form.Control
              className="auth-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
            />
          </Form.Group>
          <Form.Group id="password">
            <Form.Label>New Password</Form.Label>
            <PasswordInput className="auth-input" value={password} onChange={e => setPassword(e.target.value)} required />
          </Form.Group>
          <PasswordRequirements password={password} />
          <Form.Group id="confirmPassword">
            <Form.Label>Confirm New Password</Form.Label>
            <PasswordInput className="auth-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
          </Form.Group>
          <Button className="auth-btn" type="submit" disabled={loading}>
            Reset Password
          </Button>
        </Form>
        <Button variant="link" className="auth-link" disabled={loading} onClick={handleResend} style={{ padding: 0 }}>
          Resend code
        </Button>
        <a className="auth-link" href="/">Back to login</a>
      </div>
    </div>
  );
}
